import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react'
import { Popover } from 'antd'
import CustomIcon from '@/components/CustomIcon'
import PlusOutlined from '@ant-design/icons/PlusOutlined'
import VideoCameraAddOutlined from '@ant-design/icons/VideoCameraAddOutlined'
import classNames from 'classnames'

import chooseFileFromMaterialSpace from '@/modules/goods/utils/chooseFileFromMaterialSpace'
import { dealMaterialImage } from './helper'
import { isNumber } from '@/utils/utils'
import Upload from './Upload'
import styles from './Btn.less'

const iconMap = {
    multiplePicture: {
        component: CustomIcon,
        props: {
            type: 'icon-piliangchuantu',
            style: {
                fontSize: '28px',
                color: '#999'
            }
        }
    },
    picture: {
        component: CustomIcon,
        props: {
            type: 'icon-dantu',
            style: {
                fontSize: '32px',
                color: '#999'
            }
        }
    },
    plus: {
        component: PlusOutlined,
        props: {
            style: {
                fontSize: '28px',
                color: '#999'
            }
        }
    },
    video: {
        component: VideoCameraAddOutlined,
        props: {
            style: {
                fontSize: '26px',
                color: '#999'
            }
        }
    }
}

const MIN_WIDTH = 64 // 如果小于这个值footer将上下排布

const checkIsSupportClipboardAPI = async () => {
    if (!navigator.clipboard) {
        return false
    }

    try {
        const permissionStatus = await navigator.permissions.query({ name: 'clipboard-read' })

        if (permissionStatus.state === 'denied') {
            return false
        }

        if (navigator?.clipboard?.read) {
            return true
        }

        return false
    } catch (e) {
        console.error(e)

        if (navigator?.clipboard?.read) {
            return true
        }

        return false
    }
}

const useInnervalCheckSupportClipboardAPI = () => {
    const [isSupport, setIsSupport] = useState(false)

    useEffect(() => {
        const timer = setInterval(() => {
            checkIsSupportClipboardAPI().then(res => {
                setIsSupport(res)
            })
        }, 1000)

        return () => {
            timer && clearInterval(timer)
        }
    }, [])

    return isSupport
}

const Footer = React.memo(({ onMaterial, onLocal, onClipboard }) => {
    return (
        <>
            <div data-log-text="本地上传" className={styles.action} onClick={onLocal}>
                本地上传
            </div>
            {onMaterial ? (
                <div data-log-text="从素材空间1秒上传" className={styles.action} onClick={onMaterial}>
                    素材空间上传
                </div>
            ) : null}

            {onClipboard ? (
                <div data-log-text="剪贴板上传" className={styles.action} onClick={onClipboard}>
                    剪贴板上传
                </div>
            ) : null}
        </>
    )
})

const Btn = (
    {
        icon = 'picture',
        style,
        value,
        onChange,
        minNum,
        maxNum,
        thumbnail,
        disabled,
        multiple,
        enableToast,
        disMaterialLib,
        cloudDiskFolderId,
        accept,
        form,
        ...restProps
    },
    ref
) => {
    const $upload = useRef(null)

    const usePopover = thumbnail?.width < MIN_WIDTH

    useImperativeHandle(ref, () => $upload.current)

    const handleDrop = e => {
        if (disabled) {
            return
        }

        $upload.current && $upload.current.drop(e)
    }

    const renderIcon = () => {
        if (typeof icon === 'string') {
            const target = iconMap[icon]

            return <target.component {...target.props} />
        }

        if (typeof icon === 'function') {
            return icon({ value, maxNum })
        }

        return icon || null
    }

    const handleMaterialChange = data => {
        if (multiple && Array.isArray(value)) {
            const newImages = value.concat(
                dealMaterialImage({
                    data,
                    verify: restProps
                })
            )

            if (isNumber(maxNum)) {
                return onChange(newImages.slice(0, maxNum))
            }

            return onChange(newImages)
        }

        return onChange(
            dealMaterialImage({
                data,
                verify: restProps
            })
        )
    }

    const handleMaterial = e => {
        e.stopPropagation()

        if (disabled) {
            return
        }

        chooseFileFromMaterialSpace({
            form,
            cloudDiskFolderId,
            disMaterialLib,
            multiple,
            accept,
            onChange: handleMaterialChange
        })
    }

    const handleLocal = e => {
        e.stopPropagation()

        if (disabled) {
            return
        }

        $upload.current && $upload.current.select()
    }

    const handleClipboard = e => {
        e.stopPropagation()

        if (disabled) {
            return
        }

        $upload.current && $upload.current.readClipboardFile()
    }

    const isSupportClipboard = useInnervalCheckSupportClipboardAPI()

    const methods = {
        onMaterial: disMaterialLib && handleMaterial,
        onClipboard: isSupportClipboard && ['picture', 'multiplePicture'].includes(icon) && handleClipboard
    }

    const footer = Object.values(methods).filter(Boolean)?.length ? <Footer onLocal={handleLocal} {...methods} /> : null

    return (
        <Popover
            overlayClassName={styles.popover}
            trigger={usePopover && !disabled ? ['hover'] : []}
            content={footer}
            placement="right"
        >
            <div
                className={classNames(styles.container, {
                    [styles.isDisabled]: disabled
                })}
                style={style}
                onDrop={handleDrop}
                onDragOver={handleDrop}
                onClick={handleLocal}
            >
                {renderIcon()}

                {!usePopover && footer ? <div className={styles.footer}>{footer}</div> : null}

                <Upload
                    ref={$upload}
                    minNum={minNum}
                    maxNum={maxNum}
                    thumbnail={thumbnail}
                    value={value}
                    onChange={onChange}
                    multiple={multiple}
                    accept={accept}
                    enableToast={enableToast}
                    {...restProps}
                />
            </div>
        </Popover>
    )
}

export default forwardRef(Btn)
